class CartThreshold extends HTMLElement {
  constructor() {
    super();
    this.threshold = 0;
    this.cartTotal = 0;
    this.beforeMessage = '';
    this.afterMessage = '';
    this.emptyMessage = '';
    this.isInternational = false;
    this.isInitialized = false;
  }

  async connectedCallback() {
    this.cartTotal = parseFloat(this.getAttribute('cart-total')) || 0;
    this.beforeMessage = this.getAttribute('before-message') || '';
    this.afterMessage = this.getAttribute('after-message') || '';
    this.emptyMessage = this.getAttribute('empty-message') || '';
    
    this.isInternational = window.Shopify?.country && window.Shopify.country !== 'US';
    
    if (this.isInternational) {
      await this.waitForGlobalEApi();
      await this.loadInternationalThreshold();
    } else {
      this.threshold = parseFloat(this.getAttribute('threshold')) || 0;
    }
    
    this.isInitialized = true;
    this.render();
    
    document.addEventListener('liquid-ajax-cart:request-end', () => {
      this.updateFromCart();
    });
  }

  async waitForGlobalEApi(maxWait = 3000) {
    const startTime = Date.now();
    while (!window.globalEApi && Date.now() - startTime < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  async loadInternationalThreshold() {
    try {
      if (!window.globalEApi) {
        console.warn('GlobalE API not available, falling back to default threshold');
        this.threshold = parseFloat(this.getAttribute('threshold')) || 0;
        return;
      }

      const { countryCode, currencyCode, cultureCode } = window.globalEApi.getCurrentLocalization();
      const thresholdData = await window.globalEApi.getFreeShippingBanner(countryCode, currencyCode, cultureCode);

      if (thresholdData.IsEnabled && thresholdData.FreeShippingThreshold > 0) {
        this.threshold = thresholdData.FreeShippingThreshold;
      } else {
        this.threshold = parseFloat(this.getAttribute('threshold')) || 0;
      }
    } catch (error) {
      console.error('Failed to load international threshold:', error);
      this.threshold = parseFloat(this.getAttribute('threshold')) || 0;
    }
  }

  updateFromCart() {
    if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
      const cart = window.liquidAjaxCart.cart;
      this.cartTotal = cart.total_price / 100;
      this.render();
    }
  }

  formatMoney(money, currencyCode = null) {
    const currency = currencyCode || window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(money);
  }

  render() {
    if (!this.isInitialized) {
      return;
    }

    const isCartEmpty = this.cartTotal === 0;
    
    if (isCartEmpty) {
      this.innerHTML = `<p class="cart-threshold-empty-message">${this.emptyMessage}</p>`;
      return;
    }

    const remaining = Math.max(0, this.threshold - this.cartTotal);
    const remainingPercent = Math.min(100, (this.cartTotal / this.threshold) * 100);

    const currencyCode = this.isInternational && window.globalEApi 
      ? window.globalEApi.getCurrentLocalization().currencyCode 
      : null;
    
    const remainingMoney = this.formatMoney(remaining, currencyCode);
    
    const message = this.cartTotal >= this.threshold 
      ? this.afterMessage 
      : this.beforeMessage.replace('[[remaining_amount]]', remainingMoney);

    this.innerHTML = `
      <div class="cart-threshold-message">
        ${message}
      </div>
      <div class="cart-threshold-percent">
        <div class="cart-threshold-percent-progress" style="width: ${remainingPercent}%;"></div>
      </div>
    `;
  }

  static get observedAttributes() {
    return ['cart-total', 'threshold', 'before-message', 'after-message', 'empty-message'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    switch(name) {
      case 'cart-total':
        this.cartTotal = parseFloat(newValue) || 0;
        break;
      case 'threshold':
        this.threshold = parseFloat(newValue) || 0;
        break;
      case 'before-message':
        this.beforeMessage = newValue || '';
        break;
      case 'after-message':
        this.afterMessage = newValue || '';
        break;
      case 'empty-message':
        this.emptyMessage = newValue || '';
        break;
    }
    
    this.render();
  }
}

customElements.define('cart-threshold', CartThreshold);

